/**
 * Apply each actor's pose(t) to its scene object.
 *
 * The pose is a pure function of time, so the same applier serves both playback
 * and scrubbing — drag the clock anywhere and everything lands correctly.
 *
 * Performance shape is copied from `useEventActorMotion`: a quantized
 * last-applied cache (0.1 ft / 1 deg) means only actors that actually moved are
 * written, and `markSceneDirty()` is called ONCE per pass rather than per actor.
 *
 * Note this does NOT use `demo/masters-planner/motion/motionDriver.ts`. That is
 * a module-level singleton with a single applier slot, already owned by the
 * event planner; registering a second consumer would clobber it. The studio's
 * playback loop calls `applyAt` directly through a ref instead.
 */
import { useCallback, useEffect, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import { restPose, sampleActorPose } from '../motion/sampleActorPose'
import { sceneIdFor, type CinematicAircraft } from '../types'

export type ApplyAtFn = (tMs: number) => void

export function useStudioActorMotion(
  layerService: MapboxCustomLayerService | null,
  actors: CinematicAircraft[]
): ApplyAtFn {
  /** actorSceneId -> last applied [qx, qy, qz, qRot]. */
  const applied = useRef(new Map<string, Int32Array>())
  const actorsRef = useRef(actors)
  actorsRef.current = actors

  const applyAt = useCallback(
    (tMs: number) => {
      if (!layerService) return
      const cache = applied.current
      let dirty = false

      for (const actor of actorsRef.current) {
        const pose = actor.path
          ? sampleActorPose(actor.path, tMs, actor.position, actor.headingDeg, actor.altitudeFt)
          : restPose(actor.position, actor.headingDeg, actor.altitudeFt)

        const id = sceneIdFor(actor.id)
        const qx = Math.round(pose.position[0] * 10)
        const qy = Math.round(pose.position[1] * 10)
        const qz = Math.round(pose.position[2] * 10)
        const qr = Math.round(pose.rotationDeg)

        const prev = cache.get(id)
        if (prev && prev[0] === qx && prev[1] === qy && prev[2] === qz && prev[3] === qr) continue

        // Returns false when the model hasn't finished loading; leave the cache
        // untouched so the next tick retries.
        if (!layerService.setAircraftTransform(id, pose.position, pose.rotationDeg)) continue

        cache.set(id, Int32Array.of(qx, qy, qz, qr))
        dirty = true
      }

      if (dirty) layerService.markSceneDirty()
    },
    [layerService]
  )

  // Drop cache entries for actors that no longer exist, so an id reused later
  // can't inherit a stale "already at this pose" verdict.
  useEffect(() => {
    const live = new Set(actors.map((a) => sceneIdFor(a.id)))
    for (const id of applied.current.keys()) {
      if (!live.has(id)) applied.current.delete(id)
    }
  }, [actors])

  return applyAt
}
